import { useNavigate, useParams } from "react-router-dom";
import { useJobInfo } from "@/api/services/main/job";
import { useCaseSchema } from "@/features/case/schemas/caseSchema";
import { useForm } from "react-hook-form";
import { TCaseBasicInfo } from "@/features/case/types/case";
import { yupResolver } from "@hookform/resolvers/yup";
import { useState, useEffect, useMemo, useLayoutEffect } from "react";
import { toast } from "@/utils/toast";
import { API_SUCCESS_MESSAGE, STATUS_INACTIVE } from "@/constants/common";
import { useGetUserListPersonInCharge } from "@/api/services/main/user";
import { showError } from "@/utils/error";
import { createCase, useCaseCount, fetchCheckCaseDuplicateApi } from "@/api/services/main/case";
import { TCheckCaseDuplicateResDto, TCheckCaseDuplicateDto } from "@/api/services/main/case/dtos/check-case-duplicate.dto";
import { getDynamicRoute } from "@/utils/url";
import { AppRoutes } from "@/routes/config";
import { generateDefaultCaseName } from "@/features/case/utils/generator";
import { useDialog } from "@/hooks/useDialog";
import { normalizeString } from "@/utils/string";
import { CASE_CREATE_TALENT_STORAGE_KEY } from "@/features/case/constants/storage-keys";

export const useCaseCreateForm = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { data: jobInfoResponse, isLoading: isLoadingJobInfo } = useJobInfo(Number(jobId));
  const jobInfo = jobInfoResponse?.data ?? {};
  const activeStep = 1;
  const urlJobDetail = AppRoutes.jobDetail.replaceAll(":jobId", String(jobId));
  const { caseSchema } = useCaseSchema();

  const { data: personInChargeUsers } = useGetUserListPersonInCharge();

  const { data: caseCountResponse } = useCaseCount();
  const caseCount = (caseCountResponse?.data?.count ?? 0) + 1;
  const defaultManagementNumber = useMemo(() => {
    return String(caseCount).padStart(6, "0");
  }, [caseCount]);

  const defaultValues = useMemo(() => {
    const jobName = jobInfo.jobName || "";
    const existingCases = jobInfo.cases || [];
    const defaultCaseName = generateDefaultCaseName(jobName, existingCases);

    return {
      name: defaultCaseName,
      managementNumber: defaultManagementNumber,
      personInChargeId: undefined,
      memo: "",
      talentFilter: {},
    };
  }, [jobInfo.jobName, jobInfo.cases, defaultManagementNumber]);

  const methods = useForm<TCaseBasicInfo>({
    defaultValues,
    resolver: yupResolver<TCaseBasicInfo>(caseSchema),
    mode: "all",
    reValidateMode: "onBlur",
  });

  const { control, watch } = methods;
  const managementNumber = watch("managementNumber");
  const caseName = watch("name");

  useEffect(() => {
    if (jobInfo.jobName) {
      const jobName = jobInfo.jobName || "";
      const existingCases = jobInfo.cases || [];
      const defaultCaseName = generateDefaultCaseName(jobName, existingCases);

      methods.setValue("name", defaultCaseName);
      methods.setValue("managementNumber", defaultManagementNumber);

      // Only auto-fill personInChargeId if recruiter exists in user list and is active
      const recruiterId = jobInfo.recruiter?.id;
      if (recruiterId && personInChargeUsers.length > 0) {
        const recruiterUser = personInChargeUsers.find((u) => Number(u.id) === Number(recruiterId));
        const isRecruiterAvailable = !!recruiterUser && recruiterUser.activeStatus !== STATUS_INACTIVE;
        methods.setValue("personInChargeId", isRecruiterAvailable ? recruiterId : undefined);
      } else {
        methods.setValue("personInChargeId", undefined);
      }
    }
  }, [jobInfo.jobName, jobInfo.cases, jobInfo.recruiter?.id, methods, defaultManagementNumber, personInChargeUsers]);

  const duplicateCaseModal = useDialog();
  const [duplicateCases, setDuplicateCases] = useState<TCheckCaseDuplicateDto[]>([]);
  const [pendingCaseName, setPendingCaseName] = useState<string>("");
  const [pendingNavigateRoute, setPendingNavigateRoute] = useState<string | null>(null);

  useEffect(() => {
    if (pendingNavigateRoute) {
      navigate(pendingNavigateRoute);
      setPendingNavigateRoute(null);
    }
  }, [pendingNavigateRoute, navigate]);

  useLayoutEffect(() => {
    sessionStorage.removeItem(CASE_CREATE_TALENT_STORAGE_KEY);
  }, []);

  const handleDuplicateCaseModalCancel = () => {
    duplicateCaseModal.cancel();
  };

  const performSave = async (data: TCaseBasicInfo) => {
    if (!jobId) return;

    const stringTalents = sessionStorage.getItem(CASE_CREATE_TALENT_STORAGE_KEY) ?? "[]";
    const parsed = JSON.parse(stringTalents);
    const talentIds = (Array.isArray(parsed) ? parsed : Object.values(parsed ?? {}).flat()) as number[];

    const basicData = {
      ...data,
      name: normalizeString(data.name) ?? "",
      managementNumber: normalizeString(data.managementNumber) ?? "",
      memo: normalizeString(data.memo),
      personInChargeId: Number(data.personInChargeId),
      isSelectAll: false,
      jobId: Number(jobId),
      talents: talentIds,
    };
    try {
      const rs = await createCase(basicData);
      const caseId = rs.data?.id ?? rs.id;
      toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      if (caseId) {
        const route = getDynamicRoute(AppRoutes.caseEdit, { jobId: jobId, caseId: String(caseId) });
        setPendingNavigateRoute(route);
      } else {
        setPendingNavigateRoute(AppRoutes.caseList);
      }
    } catch (e) {
      showError(e);
    }
  };

  const onSubmit = async (data: TCaseBasicInfo) => {
    if (!jobId) return;

    // Check if any talent is selected
    const stringTalents = sessionStorage.getItem(CASE_CREATE_TALENT_STORAGE_KEY) ?? "";
    if (!stringTalents) {
      toast.error("人材を選択してください。");
      return;
    }
    try {
      const parsed = JSON.parse(stringTalents);
      const talentIds = (Array.isArray(parsed) ? parsed : Object.values(parsed ?? {}).flat()) as number[];
      if (talentIds.length === 0) {
        toast.error("人材を選択してください。");
        return;
      }
    } catch {
      toast.error("人材を選択してください。");
      return;
    }

    const caseName = data.name;

    if (caseName) {
      try {
        const caseDuplicate: TCheckCaseDuplicateResDto = await fetchCheckCaseDuplicateApi(Number(jobId), caseName);
        if (caseDuplicate?.data?.length > 0) {
          const dataCaseDuplicate = caseDuplicate.data;

          if (dataCaseDuplicate.length > 0) {
            setDuplicateCases(dataCaseDuplicate);
            setPendingCaseName(caseName);
            duplicateCaseModal.show();
            return;
          }
        }
      } catch (e) {
        showError(e);
        return;
      }
    }
    await performSave(data);
  };

  const handleNavigateBack = () => {
    navigate(urlJobDetail);
  };

  return {
    // Form methods and state
    methods,
    control,
    caseSchema,
    onSubmit,

    // Job info
    jobId,
    jobInfo,
    isLoadingJobInfo,

    // Case info
    activeStep,
    managementNumber,
    caseName,

    // Duplicate case modal
    duplicateCaseModal,
    duplicateCases,
    pendingCaseName,
    handleDuplicateCaseModalCancel,

    // Navigation
    handleNavigateBack,
  };
};
